import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FormikState } from 'formik'
import produce from 'immer'

import { useToastContext } from '../../../../../../../contexts'
import {
  DeliveryForecastDespatchAdvicePackagingBatch,
  DeliveryForecastDespatchAdvicePackagingItem,
} from '../../../../../../../data/models'
import { getHandleableError } from '../../../../../../../utils/errors/isXiaError'
import { requestUpdateDeliveryForecastDespatchAdviceItemPackagingItem } from '../../../../../data/api'
import {
  useAllDeliveryForecastDespatchAdviceItemBatches,
  useDeliveryForecastDespatchAdviceItemPackagingById,
  useDeliveryForecastDespatchAdviceItemPackagingItemById,
} from '../../../../../data/hooks'
import { FormValues } from '../../common'
import { EditForm } from './Edit.form'

type Props = {
  despatchAdviceId: string
  itemId: string
  packagingId: string
  id: string | null
  isVisible: boolean
  onHide: () => void
}

export const EditData: React.FC<Props> = ({
  despatchAdviceId,
  itemId,
  packagingId,
  id,
  isVisible,
  onHide,
}) => {
  const { t } = useTranslation()
  const { showToast } = useToastContext()

  const { data } = useDeliveryForecastDespatchAdviceItemPackagingItemById(
    despatchAdviceId,
    itemId,
    packagingId,
    id,
  )
  const { data: itemPackagings } =
    useDeliveryForecastDespatchAdviceItemPackagingById(
      despatchAdviceId,
      itemId,
      packagingId,
    )

  const { data: batches } = useAllDeliveryForecastDespatchAdviceItemBatches(
    despatchAdviceId,
    itemId,
    true,
  )

  const [isRequestRunning, setIsRequestRunning] = useState<boolean>(false)

  const handleSubmit = async (
    formData: FormValues,
    resetForm: (
      nextState?: Partial<FormikState<FormValues>> | undefined,
    ) => void,
  ) => {
    try {
      setIsRequestRunning(true)

      const packaging = dtoFromFormData(formData)

      await requestUpdateDeliveryForecastDespatchAdviceItemPackagingItem(
        despatchAdviceId,
        itemId,
        packagingId,
        id!,
        packaging,
      )

      onHide()

      resetForm()

      showToast({
        severity: 'success',
        summary: t(
          'deliveryForecastDespatchAdvice.shippingDetails.packagingTable.edit.dialog.save.messages.success.summary',
        ),
        detail:
          formData.label === 'Single'
            ? t(
                'deliveryForecastDespatchAdvice.shippingDetails.packagingTable.edit.dialog.save.messages.success.detailSingle',
                {
                  description: formData.packagingData?.Description,
                  quantity: formData.quantity,
                },
              )
            : t(
                'deliveryForecastDespatchAdvice.shippingDetails.packagingTable.edit.dialog.save.messages.success.detailMasterAndHelper',
                {
                  description: formData.packagingData?.Description,
                },
              ),
        closable: true,
      })
    } catch (error) {
      const handleableError = getHandleableError(error)
      const errorMessage = handleableError.message

      // show error toast
      showToast({
        severity: 'error',
        summary: t(
          'deliveryForecastDespatchAdvice.shippingDetails.packagingTable.add.dialog.add.messages.failure.summary',
        ),
        detail: errorMessage,
        sticky: true,
      })
    } finally {
      setIsRequestRunning(false)
    }
  }

  const dtoFromFormData = (
    formData: FormValues,
  ): DeliveryForecastDespatchAdvicePackagingItem => {
    const newPackagingData = produce(formData.packagingData, draft => {
      if (draft) {
        draft.MasterDataId = formData.packagingData?.Id
      }
    })

    return {
      Id: id!,
      ParentId: formData.selectMaster,
      PackagingType: formData.label!,
      Position: data?.Position!,
      Label: formData.label,
      PackagingId: formData.packagingData?.PackagingDataId,
      Description: formData.packagingData?.Description,
      HuNumber: formData.huNumber,
      IsOuterPackaging: formData.isOuterPackaging,
      TareWeight: formData.packagingData?.TareWeight?.Value
        ? {
            Value: formData.packagingData?.TareWeight.Value,
            Unit: formData.packagingData?.TareWeight.Unit,
          }
        : undefined,
      PackagingData: newPackagingData!,
      Quantity: {
        Value: formData.quantity,
        Unit: formData.quantityUnit,
      },
      Weight: {
        Value: formData.weight,
        Unit: formData.weightUnit,
      },
      Batches: formData.batches,
    }
  }

  const dataToInitialValues = (): FormValues => {
    let values: FormValues = {
      label: 'Master',
      huNumber: '',
      isOuterPackaging: false,
      selectMaster: undefined,
      packagingData: undefined,
      quantity: undefined,
      quantityUnit: undefined,
      weight: undefined,
      weightUnit: undefined,
      batches: [],
    }

    if (data) {
      // Set Id to the value of MasterDataId and than remove MasterDataId
      const newPackagingData = produce(data.PackagingData, draft => {
        draft.Id = draft.MasterDataId
        delete draft.MasterDataId
      })

      values = {
        ...values,
        label:
          data.Label as DeliveryForecastDespatchAdvicePackagingItem['PackagingType'],
        huNumber: data.HuNumber || '',
        isOuterPackaging: data.IsOuterPackaging || false,
        packagingData: newPackagingData,
      }

      if (data.PackagingType === 'Single') {
        let singleData = {
          ...data,
        } as DeliveryForecastDespatchAdvicePackagingItem

        values = {
          ...values,
          quantity: singleData.Quantity?.Value || 0,
          quantityUnit: singleData.Quantity?.Unit || '',
          weight: singleData?.Weight?.Value,
          weightUnit: singleData?.Weight?.Unit,
          batches: calculateBatchesOpenQuantity(data?.Batches, batches),
        }
      }

      if (data.ParentId) {
        values = {
          ...values,
          selectMaster: data.ParentId,
        }
      }
    }

    return values
  }

  return (
    <EditForm
      handleSubmit={handleSubmit}
      isRequestRunning={isRequestRunning}
      isVisible={isVisible}
      setIsVisible={onHide}
      despatchAdviceId={despatchAdviceId}
      itemId={itemId}
      data={data}
      packagingsItems={itemPackagings?.Items}
      initialValues={dataToInitialValues()}
    />
  )
}

const calculateBatchesOpenQuantity = (
  batches: DeliveryForecastDespatchAdvicePackagingBatch[] | undefined,
  allBatches: DeliveryForecastDespatchAdvicePackagingBatch[] | undefined,
) => {
  if (!batches || !allBatches) return batches || []

  const updatedBatches = batches.map(batch => {
    const correspondingBatch = allBatches.find(
      allBatch => allBatch.Id === batch.BatchId,
    )
    if (correspondingBatch) {
      return {
        ...batch,
        OpenQuantity: correspondingBatch.Quantity,
      }
    }
    return batch
  })

  return updatedBatches
}
